import { Company, Referrer, Application } from 'types';
const countCommonCharacters = (string1: String, string2: String): number => {
  let count = 0;
  const charCount: { [char: string]: number } = {};
  for (const char of string1) {
    charCount[char] = (charCount[char] || 0) + 1;
  }
  for (const char of string2) {
    if (charCount[char] > 0) {
      count++;
      charCount[char]--;
    }
  }
  return count;
};
const similarityScore = (company: string, searchQuery: string): number => {
  company = company.toLowerCase();
  searchQuery = searchQuery.toLowerCase();
  let score = 0;
  const minLen = Math.min(company.length, searchQuery.length);

  for (let i = 0; i < minLen; i++) {
    if (company[i] === searchQuery[i]) {
      score += 10;
    } else {
      break;
    }
  }
  score += countCommonCharacters(searchQuery, company);
  return company.includes(searchQuery)
    ? score + 10 * searchQuery.length
    : score;
};
export const sortByStringSimilarity = async (
  searchString: string,
  companies: Company[]
): Promise<Company[]> => {
  const sortedCompanies = [...companies];
  if (searchString.trim() !== '') {
    sortedCompanies.sort(
      (a, b) =>
        similarityScore(b.company, searchString) -
        similarityScore(a.company, searchString)
    );
  } else {
    sortedCompanies.sort((a, b) => {
      if (a.company < b.company) {
        return -1;
      }
      if (a.company > b.company) {
        return 1;
      }
      return 0;
    });
  }
  return sortedCompanies;
};

const NEW_REFERRER_SCORE = 10;
const REFERRER_3DAY_SCORE = 15;
const REFERRER_7DAY_SCORE = 9;
const REFERRER_30DAY_SCORE = 5;
const APPLICATION_PENDING_SCORE = -2.5;
const APPLICATION_FINISHED_SCORE = 1;
export const sortByActivityLevel = async (
  referrers: Referrer[],
  applications?: Application[] | null
): Promise<Referrer[]> => {
  const now = Date.now();
  const threeDaysAgo = now - 3 * 24 * 60 * 60 * 1000;
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

  const scoredReferrers = referrers.map(ref => {
    const relatedApps = (applications ?? []).filter(app => app.referrerUid === ref.uid);

    // Count pending apps older than 3 days
    const pendingCount = relatedApps.filter(app => {
      const createdAt = app.createdAt.toDate().getTime();
      return app.status === "reviewing" && createdAt < threeDaysAgo;
    }).length;

    // Calculate score
    let score = 0;

    if (ref.createdAt) {
      const createdAt = ref.createdAt.toDate().getTime();
      if (createdAt > sevenDaysAgo) {
        score += NEW_REFERRER_SCORE;
      }
    }

    const lastReferredDate = ref.lastReferredDate?.toDate().getTime();
    if (lastReferredDate != null) {
      if (lastReferredDate > threeDaysAgo) {
        score += REFERRER_3DAY_SCORE;
      } else if (lastReferredDate > sevenDaysAgo) {
        score += REFERRER_7DAY_SCORE;
      } else if (lastReferredDate > thirtyDaysAgo) {
        score += REFERRER_30DAY_SCORE;
      }
    }

    score += pendingCount * APPLICATION_PENDING_SCORE;
    const finishedCount = Math.max((ref.applicationCount ?? 0) - pendingCount, 0);
    score += finishedCount * APPLICATION_FINISHED_SCORE;
    return { ...ref, pendingCount, finishedCount, score };
  });
  const sorted = scoredReferrers.sort((a, b) => b.score - a.score);
  return sorted.map(({ score, pendingCount, finishedCount, ...ref }) => ref);
};